/**
 * Audit Planning Tool Page (EX9)
 * 
 * Central hub for audit planning with 4 tabs:
 * - Testing Timeline: Timeline-based planning (TBD)
 * - Planning Documents: Document repository
 * - Applications: Financial applications inventory
 * - Change Indicator Checklist: Aggregated checklist responses & messages
 */

import React, { useState, useMemo, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { Typography, Tabs } from 'antd';
import styled from 'styled-components';
import { RootState } from '../../../store';
import ChangeLogButton from '../changeLog/ChangeLogButton';
import ExportButton from '../../../components/common/ExportButton';

// Tab Components
import TestingTimelineTab from './tab1/TestingTimelineTab';
import PlanningDocumentsTab from './tab2/PlanningDocumentsTab';
import ApplicationsTab from './tab3/ApplicationsTab';
import ChangeIndicatorChecklistTab from './tab4/ChangeIndicatorChecklistTab';
import NotFoundPage from '../../NotFoundPage';

// Icons
import TimelineIcon from '../../../assets/icons/ic-timeline.svg?react';
import DocumentIcon from '../../../assets/icons/ic-document.svg?react';
import ApplicationsIcon from '../../../assets/icons/ic-applications.svg?react';
import ListChecksIcon from '../../../assets/icons/ic-list-checks.svg?react';

const { Title } = Typography;

// ========================================
// Styled Components
// ========================================

const PageContainer = styled.div`
  padding: 0;
`;

const PageHeader = styled.div`
  margin-bottom: 24px;
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const PageTitle = styled(Title)`
  margin: 0 !important;
  font-family: Inter !important;
  font-weight: 500 !important;
  font-size: 18px !important;
  line-height: 100% !important;
  letter-spacing: 0.005em !important;
  color: #1F2937 !important;
`;

const StyledTabs = styled(Tabs)`
  .ant-tabs-nav {
    margin-bottom: 24px;
  }

  .ant-tabs-tab {
    padding: 12px 16px;
    
    .ant-tabs-tab-btn {
      font-family: Inter;
      font-weight: 500;
      font-size: 14px;
      color: #6B7280;
    }

    svg path,
    svg circle {
      stroke: #6B7280;
      transition: stroke 0.3s;
    }

    &.ant-tabs-tab-active {
      .ant-tabs-tab-btn {
        color: #2563EB;
      }

      svg path,
      svg circle {
        stroke: #2563EB;
      }
    }

    &:hover {
      .ant-tabs-tab-btn {
        color: #2563EB;
      }

      svg path,
      svg circle {
        stroke: #2563EB;
      }
    }
  }

  .ant-tabs-ink-bar {
    background: #2563EB;
  }
`;

const TabLabel = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;

  svg {
    width: 20px;
    height: 20px;
  }
`;

const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

// ========================================
// Constants
// ========================================

// Valid tab keys
const TAB_KEYS = ['timeline', 'documents', 'applications', 'checklist'] as const;
type TabKey = typeof TAB_KEYS[number];

// ========================================
// Component
// ========================================

const AuditPlanPage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  
  // Auth state - isAdmin is computed in authSlice when user is set
  const { isAdmin, isExternalUser } = useSelector((state: RootState) => state.auth);
  const { selectedReport } = useSelector((state: RootState) => state.annualReport);
  
  // Export trigger state
  const [triggerExport, setTriggerExport] = useState(false);
  
  // Access control: Only admins can access this page
  // Process Owners will see a 404 page
  if (!isAdmin && !isExternalUser) {
    return <NotFoundPage />;
  }
  
  // Get current tab from URL or default
  const currentTab = useMemo(() => {
    const tabParam = searchParams.get('tab');
    if (tabParam && TAB_KEYS.includes(tabParam as TabKey)) {
      return tabParam;
    }
    return 'timeline'; // Default tab
  }, [searchParams]);
  
  // Handle tab change - update URL
  const handleTabChange = useCallback((key: string) => {
    setSearchParams({ tab: key }, { replace: true });
  }, [setSearchParams]);
  
  // Export handlers
  const handleExportClick = useCallback(() => {
    setTriggerExport(true);
  }, []);
  
  const handleExportClose = useCallback(() => {
    setTriggerExport(false);
  }, []);
  
  // Tab items configuration
  const tabItems = [
    {
      key: 'timeline',
      label: (
        <TabLabel>
          <TimelineIcon />
          Testing Timeline
        </TabLabel>
      ),
      children: <TestingTimelineTab />,
    },
    {
      key: 'documents',
      label: (
        <TabLabel>
          <DocumentIcon />
          Planning Documents
        </TabLabel>
      ),
      children: <PlanningDocumentsTab />,
    },
    {
      key: 'applications',
      label: (
        <TabLabel>
          <ApplicationsIcon />
          Applications
        </TabLabel>
      ),
      children: (
        <ApplicationsTab
          triggerExport={currentTab === 'applications' ? triggerExport : false}
          onExportClose={handleExportClose}
        />
      ),
    },
    {
      key: 'checklist',
      label: (
        <TabLabel>
          <ListChecksIcon />
          Change Indicators
        </TabLabel>
      ),
      children: <ChangeIndicatorChecklistTab isAdmin={isAdmin} />,
    },
  ];

  return (
    <PageContainer>
      <PageHeader>
        <PageTitle level={2}>Audit Planning Tool</PageTitle>
        <HeaderActions>
          {currentTab === 'applications' && (
            <ExportButton onClick={handleExportClick} />
          )}
          {selectedReport && isAdmin && (
            <ChangeLogButton
              actionType="auditPlan"
              annualReportId={selectedReport.annualReportId}
            />
          )}
        </HeaderActions>
      </PageHeader>

      <StyledTabs
        activeKey={currentTab}
        onChange={handleTabChange}
        items={tabItems}
        destroyInactiveTabPane={false}
      />

    </PageContainer>
  );
};

export default AuditPlanPage;
